import { useEffect, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';

import chillImg from 'assets/brand/chill@2x.png';
import asBentoPage from 'common/bento/hoc/asBentoPage/asBentoPage';
import webView from 'common/webView';
import Heading from 'components/_core/Heading';
import ProgressBar from 'components/_core/ProgressBar';
import { OBCard } from 'features/Bento/components';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import locales from './locales';

const PROGRESS_TIMEOUT = 1000;
const PROCESSING_TIMEOUT = PROGRESS_TIMEOUT + 2000;
const TOO_LONG_TIMEOUT = PROCESSING_TIMEOUT + 2000;
const WAY_TOO_LONG_TIMEOUT = TOO_LONG_TIMEOUT + 4000;

const DONE_TIMEOUT = 1500;

interface SasuCreationPendingProps {
  hasProcessedInfo: boolean;
  onDone: () => void;
}

const SasuCreationPending = asBentoPage<SasuCreationPendingProps>(
  ({ hasProcessedInfo, Layout, onDone }) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [takingLonger, setTakingLonger] = useState(false);
    const [takingEvenLonger, setTakingEvenLonger] = useState(false);
    const [progress, setProgress] = useState(0.035);

    useEffect(() => {
      if (hasProcessedInfo) {
        return () => {};
      }

      const progressTimeoutId = setTimeout(() => {
        setProgress(0.2);
      }, PROGRESS_TIMEOUT);

      const processingTimeoutId = setTimeout(() => {
        setIsProcessing(true);
        setProgress(0.5);
      }, PROCESSING_TIMEOUT);

      const tooLongTimeoutId = setTimeout(() => {
        setTakingLonger(true);
        setProgress(0.75);
      }, TOO_LONG_TIMEOUT);

      const wayTooLongTimeoutId = setTimeout(() => {
        setTakingEvenLonger(true);
      }, WAY_TOO_LONG_TIMEOUT);

      return () => {
        clearTimeout(progressTimeoutId);
        clearTimeout(processingTimeoutId);
        clearTimeout(tooLongTimeoutId);
        clearTimeout(wayTooLongTimeoutId);
      };
    }, [hasProcessedInfo]);

    useEffect(() => {
      if (!hasProcessedInfo) {
        return () => {};
      }

      setProgress(1);
      const doneTimer = setTimeout(onDone, DONE_TIMEOUT);

      return () => {
        clearTimeout(doneTimer);
      };
    }, [hasProcessedInfo, onDone]);

    let description = locales.eta;
    if (takingEvenLonger) {
      description = locales.takingEvenLonger;
    } else if (takingLonger) {
      description = locales.takingLonger;
    }

    return (
      <Layout
        nav={webView.config?.isEmbedInAppLayout ? null : <OBNavigation />}
      >
        <OBCard>
          <OBCard.Illustration src={chillImg} />

          <OBCard.Body paddingBottom="space-48">
            <Flex alignItems="center" flexDirection="column">
              <Heading marginBottom="space-16" size="lg">
                {isProcessing ? locales.processing : locales.analyzing}
              </Heading>
              <Box marginBottom="space-16" maxWidth="272px" width="100%">
                <ProgressBar
                  barProps={{
                    transition: 'flex 1s ease-out',
                  }}
                  ratio={progress}
                />
              </Box>
              <Box position="relative">
                <AnimatePresence initial={false}>
                  <Text
                    animate={{ opacity: 1, y: 0 }}
                    as={motion.p}
                    exit={{ opacity: 0, position: 'absolute', y: -10 }}
                    initial={{ opacity: 0, y: 10 }}
                    key={description}
                    textAlign="center"
                    width="100%"
                  >
                    {description}
                  </Text>
                </AnimatePresence>
              </Box>
            </Flex>
          </OBCard.Body>
        </OBCard>
      </Layout>
    );
  },
);

export default SasuCreationPending;
